// RichTextEditor.js
import React, { useEffect, useRef, useState } from 'react';
import { EditorContent, useEditor } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import TextStyle from '@tiptap/extension-text-style';
import Color from '@tiptap/extension-color';
import TextAlign from '@tiptap/extension-text-align';
import Heading from '@tiptap/extension-heading';
import Underline from '@tiptap/extension-underline';
import html2canvas from 'html2canvas';
import { Save } from 'lucide-react';
import PropTypes from 'prop-types';

export default function RichTextEditor({
  pageId,
  selectedLanguage,
  onImageGenerated,
  onContentSave,
}) {
  const containerRef = useRef(null);
  const [initialHtml, setInitialHtml] = useState('');
  const [loading, setLoading] = useState(true);
  const [fontSize, setFontSize] = useState('14');
  const [fontFamily, setFontFamily] = useState('Futura');
  const [customFontSize, setCustomFontSize] = useState('');

  // Fetch content from API on load
  useEffect(() => {
    const fetchContent = async () => {
      try {
        const response = await fetch(`/api/page/${pageId}`);
        const data = await response.json();
        const field = `content_html_${selectedLanguage}`;
        setInitialHtml(data[field] || '');
      } catch (err) {
        console.error('Error loading content:', err);
      } finally {
        setLoading(false);
      }
    };

    if (pageId && selectedLanguage) {
      fetchContent();
    }
  }, [pageId, selectedLanguage]);

  const editor = useEditor({
    extensions: [
      StarterKit.configure({ heading: false }),
      Underline,
      TextStyle,
      Color,
      Heading.configure({ levels: [1, 2, 3] }),
      TextAlign.configure({ types: ['heading', 'paragraph'] }),
    ],
    content: initialHtml || '',
    editorProps: {
      attributes: {
        style: `font-size: ${fontSize}px; font-family: ${fontFamily};`,
      },
    },
    onCreate: ({ editor }) => {
      // Set default font style when editor is ready
      editor.commands.setMark('textStyle', {
        fontSize: `${fontSize}px`,
        fontFamily: fontFamily,
      });
    },
  });

  useEffect(() => {
    if (editor && initialHtml) {
      editor.commands.setContent(initialHtml);
    }
  }, [editor, initialHtml]);

  const applyFontSize = (size) => {
    const normalizedSize = String(size);
    setFontSize(normalizedSize);
    setCustomFontSize(normalizedSize);
    editor
      .chain()
      .focus()
      .setMark('textStyle', { fontSize: `${normalizedSize}px` })
      .run();
  };

  const handleCustomFontSizeChange = (value) => {
    setCustomFontSize(value);
    const parsedValue = Number(value);
    if (!Number.isFinite(parsedValue) || parsedValue <= 0) return;
    applyFontSize(parsedValue);
  };

  const applyFontFamily = (font) => {
    setFontFamily(font);
    editor.chain().focus().setMark('textStyle', { fontFamily: font }).run();
  };

  const handleAddImage = async () => {
    const editorElement = containerRef.current?.querySelector('.tiptap');
    if (!editorElement) return;

    const wrapper = document.createElement('div');
    wrapper.style.display = 'inline-block';
    wrapper.style.padding = '0 10px 17px 10px';
    wrapper.style.background = 'white';
    wrapper.style.lineHeight = '2';
    wrapper.style.fontFamily = fontFamily;
    wrapper.style.color = 'black';
    wrapper.style.whiteSpace = 'pre-wrap';
    wrapper.style.textAlign = 'justify';
    wrapper.style.boxSizing = 'border-box';
    wrapper.style.fontSize = `${fontSize}px`;

    const clone = editorElement.cloneNode(true);
    clone.style.maxWidth = '322px';
    wrapper.appendChild(clone);
    document.body.appendChild(wrapper);

    try {
      const canvas = await html2canvas(wrapper, {
        backgroundColor: null,
        scale: 2,
      });
      const dataURL = canvas.toDataURL('image/png');
      const img = new window.Image();
      img.src = dataURL;
      img.onload = () => onImageGenerated(img);
    } catch (error) {
      console.error('Error rendering to canvas:', error);
    } finally {
      document.body.removeChild(wrapper);
    }
  };

  const handleSaveContent = () => {
    if (!editor) return;
    const html = editor.getHTML();
    onContentSave(html, selectedLanguage);
  };

  if (loading || !editor) return <div>Loading editor...</div>;

  return (
    <div className="p-4 border border-gray-300 rounded bg-white mt-4">
      <div className="flex flex-wrap gap-2 mb-4 items-center">
        <button
          onClick={() => editor.chain().focus().toggleBold().run()}
          className="px-3 py-1 border rounded text-sm font-medium hover:bg-gray-100"
        >
          Bold
        </button>
        <button
          onClick={() => editor.chain().focus().toggleUnderline().run()}
          className="px-3 py-1 border rounded text-sm font-medium hover:bg-gray-100"
        >
          Underline
        </button>
        <button
          onClick={() => editor.chain().focus().toggleItalic().run()}
          className="px-3 py-1 border rounded text-sm font-medium hover:bg-gray-100"
        >
          Italic
        </button>

        {/* Font Family Selector */}
        <select
          onChange={(e) => applyFontFamily(e.target.value)}
          value={fontFamily}
          className="px-2 py-1 border rounded text-sm"
        >
          <option value="Futura">Futura</option>
          <option value="Didact Gothic">Didact Gothic</option>
          <option value="Questrial">Questrial</option>
          <option value="Arial">Arial</option>
          <option value="Georgia">Georgia</option>
          <option value="Times New Roman">Times New Roman</option>
          <option value="Courier New">Courier New</option>
        </select>

        {/* Font Size Selector */}
        <select
          onChange={(e) => applyFontSize(e.target.value)}
          value={fontSize}
          className="px-2 py-1 border rounded text-sm"
        >
          <option value="12">12px</option>
          <option value="14">14px</option>
          <option value="16">16px</option>
          <option value="18">18px</option>
          <option value="24">24px</option>
          <option value="80">80px</option>
          <option value="90">90px</option>
          <option value="100">100px</option>
          <option value="120">120px</option>
          <option value="140">140px</option>
        </select>

        <input
          type="number"
          min="1"
          step="1"
          inputMode="numeric"
          value={customFontSize}
          onChange={(e) => handleCustomFontSizeChange(e.target.value)}
          placeholder="Custom px"
          className="w-28 px-2 py-1 border rounded text-sm"
        />
      </div>

      <div
        ref={containerRef}
        className="border border-gray-200 rounded p-4 min-h-[200px] text-base leading-relaxed"
      >
        <EditorContent editor={editor} />
      </div>

      <div className="flex gap-4 mt-4">
        <button
          onClick={handleSaveContent}
          className="p-2 bg-white rounded shadow hover:bg-gray-100 flex justify-center items-center"
        >
          <Save size={20}></Save>
        </button>
        <button
          onClick={handleAddImage}
          className="bg-gray-500 text-white hover:bg-gray-600 font-bold py-2 px-4 rounded"
        >
          Insert in image
        </button>
      </div>
    </div>
  );
}

RichTextEditor.propTypes = {
  pageId: PropTypes.string.isRequired,
  selectedLanguage: PropTypes.string.isRequired,
  onImageGenerated: PropTypes.func, // optional callback
  onContentSave: PropTypes.func.isRequired, // assuming this is mandatory
};
